import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { initSounds, playSoundIfEnabled } from '../utils/soundEffects';
import SoundToggle from './SoundToggle';

const { 
  FiArrowLeft, FiHeart, FiTarget, FiRefreshCw, FiShuffle, 
  FiInfo, FiEye, FiEyeOff, FiClock, FiCheck, FiX, FiEdit, FiUsers 
} = FiIcons;

const TruthOrDareGame = () => {
  const navigate = useNavigate();
  const [currentCard, setCurrentCard] = useState(null);
  const [cardType, setCardType] = useState(null); // 'truth' or 'dare'
  const [usedCards, setUsedCards] = useState({ truth: [], dare: [] });
  const [isFlipped, setIsFlipped] = useState(false);
  const [filter, setFilter] = useState('mild'); // 'mild', 'spicy', 'hot', 'extreme'
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [player1, setPlayer1] = useState('Player 1');
  const [player2, setPlayer2] = useState('Player 2');
  const [activePlayer, setActivePlayer] = useState('player1');
  const [showPlayerSetup, setShowPlayerSetup] = useState(false);
  const [showCustomCard, setShowCustomCard] = useState(false);
  const [customCardText, setCustomCardText] = useState('');
  const [customCardType, setCustomCardType] = useState('truth');
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [taskCompleted, setTaskCompleted] = useState(false);
  const [playerStats, setPlayerStats] = useState({
    player1: { truth: 0, dare: 0 },
    player2: { truth: 0, dare: 0 }
  });

  // Initialize sounds when component mounts
  useEffect(() => {
    initSounds();
  }, []);

  const truthCards = {
    mild: [
      "Điều gì khiến bạn cảm thấy hạnh phúc nhất khi ở bên người kia?",
      "Bạn nhớ lần đầu tiên gặp nhau như thế nào?",
      "Điều gì bạn thích nhất về ngoại hình của đối phương?",
      "Bạn có từng mơ thấy người này không? Mơ gì?",
      "Nếu chỉ còn một ngày để sống, bạn muốn làm gì với người này?",
      "Bạn nghĩ điều gì làm cho mối quan hệ này đặc biệt?",
      "Kỷ niệm nào với người này khiến bạn không thể quên?",
      "Bạn muốn đi du lịch đâu cùng người này nhất?",
      "Điều gì về tính cách của đối phương thu hút bạn nhất?",
      "Bạn có tin vào tình yêu đích thực không? Tại sao?",
      "Lần đầu tiên bạn nghĩ 'mình yêu người này' là khi nào?",
      "Bạn thích được gọi bằng biệt danh gì bởi người này?",
      "Điều gì khiến bạn cảm thấy gần gũi nhất với đối phương?",
      "Bạn có sợ mất người này không? Tại sao?",
      "Món quà ý nghĩa nhất bạn muốn tặng cho người này là gì?",
      "Bạn nghĩ mình và người này sẽ như thế nào sau 10 năm nữa?",
      "Điều gì khiến bạn tin tưởng hoàn toàn vào người này?",
      "Khoảnh khắc nào bạn cảm thấy tự hào nhất về đối phương?",
      "Bạn muốn học được điều gì từ người này?",
      "Nếu viết một bài thơ về người này, câu đầu tiên sẽ là gì?"
    ],
    spicy: [
      "Bộ phận nào trên cơ thể đối phương khiến bạn bị kích thích nhất?",
      "Bạn có từng có ảo tưởng tình dục về người này trước khi ở bên họ không?",
      "Điều gì về người này khiến bạn muốn 'ăn thịt' họ nhất?",
      "Nơi nào trên cơ thể bạn muốn được đối phương chạm vào nhất?",
      "Bạn thích kiểu hôn nào của đối phương nhất?",
      "Thời điểm nào trong ngày bạn muốn 'yêu' người này nhất?",
      "Bạn có thích khi đối phương chủ động không? Tại sao?",
      "Điều gì về giọng nói của người này khiến bạn 'rung rinh'?",
      "Bạn muốn được đối phương massage ở đâu nhất?",
      "Trang phục nào của người này khiến bạn không thể rời mắt?",
      "Bạn có thích khi đối phương thì thầm vào tai không? Vì sao?",
      "Mùi hương nào trên cơ thể người này khiến bạn 'say mê'?",
      "Bạn thích nhìn đối phương ở góc độ nào nhất?",
      "Điều gì khiến bạn muốn hôn người này ngay lập tức?",
      "Bạn có bao giờ 'tự sướng' khi nghĩ về người này không?",
      "Thời gian nào trong ngày bạn cảm thấy 'thèm' người này nhất?",
      "Bạn muốn thấy người này mặc gì khi ở nhà?",
      "Điều gì về cách di chuyển của đối phương khiến bạn hứng thú?",
      "Bạn có muốn thử một điều 'táo bạo' nào với người này không?",
      "Khi nhìn vào mắt người này, bạn nghĩ gì đầu tiên?"
    ],
    hot: [
      "Tư thế yêu nào bạn muốn thử nhất với người này?",
      "Bạn có từng 'tự làm mình sướng' khi nghĩ về người này không? Kể chi tiết.",
      "Nơi nào bạn muốn 'quan hệ' với đối phương nhất?",
      "Điều 'dâm đãng' nhất bạn muốn làm với người này là gì?",
      "Bạn thích được đối phương 'thống trị' hay 'thống trị' họ?",
      "Giấc mơ 18+ nóng bỏng nhất về người này là gì? Kể chi tiết.",
      "Bạn có muốn quay video 'yêu đương' với người này không? Tại sao?",
      "Đồ chơi tình dục nào bạn muốn dùng với đối phương?",
      "Bạn thích 'oral sex' hay 'penetration' hơn? Giải thích lý do.",
      "Nơi công cộng nào bạn muốn 'làm chuyện ấy' với người này?",
      "Bạn có thích roleplay không? Muốn đóng vai gì?",
      "Điều 'kinky' nhất bạn muốn thử với đối phương là gì?",
      "Bạn có muốn bị trói hay trói người này không? Tại sao?",
      "Thời gian 'yêu' lâu nhất bạn muốn với người này là bao lâu?",
      "Bạn thích 'rough sex' hay 'gentle sex' hơn? Vì sao?",
      "Câu nói 'dirty talk' bạn muốn nghe từ người này nhất?",
      "Bạn có muốn thử 'threesome' với người này không? Với ai?",
      "Vị trí nào trên giường bạn thích nhất khi với đối phương?",
      "Bạn có thích khi đối phương 'xuất tinh' lên người mình không?",
      "Điều 'cấm kỵ' nhất bạn muốn phá vỡ với người này là gì?"
    ],
    extreme: [
      "Mô tả chi tiết lần quan hệ tình dục tuyệt vời nhất của bạn với đối phương.",
      "Nếu được phép làm bất cứ điều gì với đối phương trong 1 giờ, bạn sẽ làm gì?",
      "Fantasy tình dục đen tối nhất của bạn với người này là gì?",
      "Bạn đã bao giờ giả vờ lên đỉnh không? Khi nào và tại sao?",
      "Bạn sẽ phản ứng thế nào nếu đối phương muốn thử BDSM cực đoan?",
      "Nếu được chọn 3 người khác để cùng quan hệ với đối phương, bạn sẽ chọn ai?",
      "Bạn đã từng ngoại tình hoặc có ý định ngoại tình chưa?",
      "Điều gì khiến bạn cực kỳ hưng phấn mà bạn chưa từng nói với ai?",
      "Bạn đã bao giờ quay lén video sex của mình chưa?",
      "Bạn muốn đối phương làm gì để khiến bạn đạt khoái cảm cao nhất?",
      "Bạn thích bị đánh đòn không? Ở đâu và như thế nào?",
      "Bạn có muốn thử 'golden shower' hoặc các fetish khác không?",
      "Nếu được mời tham gia một sex party, bạn có đi không? Với điều kiện gì?",
      "Bạn có muốn nhìn đối phương quan hệ với người khác không?",
      "Bạn đã từng lén lút sử dụng đồ chơi tình dục khi đối phương không biết?",
      "Bạn có fantasies về việc quan hệ với người cùng giới không?",
      "Bạn có thích bị bịt mắt và để nhiều người chạm vào cơ thể mình không?",
      "Nếu được trả 1 tỷ đồng để quay một video sex công khai, bạn có làm không?",
      "Bạn có muốn thử quan hệ ở nơi có thể bị người khác nhìn thấy không?",
      "Điều gì về đối phương khiến bạn cảm thấy ghê tởm về mặt tình dục?"
    ]
  };

  const dareCards = {
    mild: [
      "Hôn đối phương trong 15 giây liên tục",
      "Massage vai và cổ cho đối phương trong 2 phút",
      "Nhảy một điệu nhảy chậm cùng nhau trong 1 phút",
      "Hát một bài hát tình yêu cho đối phương nghe",
      "Ôm chặt đối phương và thì thầm 3 điều yêu thích về họ",
      "Hôn khắp khuôn mặt đối phương",
      "Vẽ trái tim lên lưng tay đối phương bằng son môi",
      "Massage chân cho đối phương trong 2 phút",
      "Nhìn vào mắt nhau trong 30 giây không được cười",
      "Cho đối phương ăn một món gì đó bằng tay",
      "Chụp 5 bức ảnh đôi thật ngọt ngào",
      "Kể một câu chuyện tình yêu ngắn có hai người làm nhân vật chính",
      "Thực hiện một màn khiêu vũ đơn giản",
      "Hôn lên trán và thì thầm lời yêu thương",
      "Tạo dáng thật lãng mạn và giữ trong 30 giây",
      "Massage đầu cho đối phương trong 3 phút",
      "Viết một tin nhắn tình yêu và đọc to",
      "Ôm đối phương từ phía sau trong 1 phút",
      "Hôn tay đối phương như một quý ông/quý cô",
      "Cùng nhau uống một ly nước bằng cách dùng chung ống hút"
    ],
    spicy: [
      "Hôn cổ đối phương trong 30 giây",
      "Cởi một món đồ của mình một cách quyến rũ",
      "Massage lưng cho đối phương và hôn dọc theo cột sống",
      "Dùng lưỡi liếm từ tai xuống cổ đối phương",
      "Nhảy lap dance cho đối phương trong 1 phút",
      "Hôn và cắn nhẹ tai đối phương",
      "Vuốt ve toàn thân đối phương trong 2 phút",
      "Thì thầm điều khiêu khích nhất vào tai đối phương",
      "Dùng đá lạnh vuốt ve từ cổ xuống ngực đối phương",
      "Hôn môi say đắm trong 45 giây",
      "Cởi áo của đối phương chỉ bằng răng",
      "Massage ngực/lưng của đối phương bằng dầu",
      "Dùng lưỡi vẽ một đường từ cổ xuống vai",
      "Để đối phương cắn nhẹ khắp cơ thể bạn",
      "Thực hiện striptease nhẹ nhàng",
      "Hôn từ tay lên vai rồi xuống ngực",
      "Dùng lông vũ vuốt ve cơ thể đối phương",
      "Massage mông cho đối phương trong 1 phút",
      "Liếm chocolate từ cơ thể đối phương",
      "Để đối phương thổi nhẹ lên vùng nhạy cảm"
    ],
    hot: [
      "Cởi hết đồ và để đối phương ngắm trong 1 phút",
      "Dùng miệng khám phá vùng nhạy cảm của đối phương trong 2 phút",
      "Làm đối phương thỏa mãn bằng tay cho đến khi họ yêu cầu dừng",
      "Thực hiện oral sex cho đối phương trong 3 phút",
      "Cởi đồ và nhảy lap dance hoàn toàn khỏa thân",
      "Để đối phương trói tay bạn và làm bất cứ gì họ muốn trong 5 phút",
      "Tự làm mình thỏa mãn trước mặt đối phương",
      "Dùng lưỡi liếm khắp cơ thể đối phương từ trên xuống dưới",
      "Thực hiện tư thế 69 trong 2 phút",
      "Để đối phương 'sử dụng' bạn theo cách họ muốn trong 3 phút",
      "Mô phỏng quan hệ tình dục với đối phương trong 2 phút",
      "Cởi đồ và tạo dáng theo yêu cầu của đối phương",
      "Dùng miệng làm đối phương đạt cực khoái",
      "Thực hiện một tư thế quan hệ mà hai người chưa từng thử",
      "Để đối phương 'tra tấn' khoái cảm cho bạn trong 4 phút",
      "Quan hệ bằng miệng đồng thời cho nhau",
      "Thực hiện vai trò 'slave' cho đối phương trong 10 phút",
      "Dùng đồ chơi tình dục trên cơ thể đối phương",
      "Quan hệ tình dục ở một tư thế do đối phương chọn",
      "Để đối phương làm bất cứ điều gì họ muốn với cơ thể bạn trong 5 phút"
    ],
    extreme: [
      "Quan hệ tình dục với đối phương ngay bây giờ, hoàn toàn theo cách họ muốn",
      "Để đối phương buộc bạn và làm 'nô lệ tình dục' trong 15 phút",
      "Thực hiện 3 tư thế khác nhau trong 10 phút và đạt cực khoái",
      "Khám phá toàn bộ cơ thể đối phương bằng miệng và làm họ đạt cực khoái",
      "Thực hiện một fantasy tình dục mà đối phương luôn muốn thử",
      "Làm đối phương đạt cực khoái 3 lần liên tiếp",
      "Chụp 5 bức ảnh khỏa thân gợi cảm theo tư thế đối phương chỉ định",
      "Thử một fetish mà đối phương đề xuất",
      "Đặt một đồ chơi tình dục vào bên trong và để đối phương điều khiển",
      "Dùng thức ăn để kích thích vùng nhạy cảm của đối phương và ăn sạch",
      "Thực hiện role-play với trang phục gợi cảm theo chủ đề đối phương chọn",
      "Để đối phương trói tay chân và làm bất cứ điều gì họ muốn trong 10 phút",
      "Thử anal sex nếu chưa từng thử, hoặc một tư thế anal mới nếu đã từng",
      "Dùng đá lạnh và nước nóng luân phiên khi kích thích đối phương",
      "Tự quay video thủ dâm và gửi cho đối phương sau buổi chơi",
      "Quan hệ tình dục ở nơi có thể bị phát hiện",
      "Làm theo mọi mệnh lệnh tình dục của đối phương trong 30 phút",
      "Thực hiện kịch bản BDSM hoàn chỉnh với đối phương",
      "Mặc trang phục cosplay khiêu dâm và thực hiện vai diễn đó",
      "Thử một tư thế Kama Sutra cực kỳ khó và duy trì trong 5 phút"
    ]
  };

  // Add new challenges that require timers
  const timedDares = {
    mild: [
      { text: "Nhìn vào mắt nhau trong 60 giây không được cười", time: 60 },
      { text: "Massage vai cho đối phương trong 2 phút", time: 120 },
      { text: "Ôm nhau thật chặt trong 1 phút", time: 60 },
      { text: "Nhảy một điệu nhảy chậm cùng nhau trong 1 phút", time: 60 }
    ],
    spicy: [
      { text: "Hôn cổ đối phương trong 45 giây", time: 45 },
      { text: "Massage lưng cho đối phương trong 2 phút", time: 120 },
      { text: "Vuốt ve toàn thân đối phương trong 3 phút", time: 180 },
      { text: "Hôn môi say đắm trong 1 phút", time: 60 }
    ],
    hot: [
      { text: "Dùng miệng khám phá vùng nhạy cảm của đối phương trong 2 phút", time: 120 },
      { text: "Tự làm mình thỏa mãn trước mặt đối phương trong 3 phút", time: 180 },
      { text: "Thực hiện tư thế 69 trong 3 phút", time: 180 },
      { text: "Để đối phương 'sử dụng' bạn theo cách họ muốn trong 5 phút", time: 300 }
    ],
    extreme: [
      { text: "Để đối phương buộc bạn và làm 'nô lệ tình dục' trong 7 phút", time: 420 },
      { text: "Quan hệ tình dục với 3 tư thế khác nhau trong 10 phút", time: 600 },
      { text: "Làm theo mọi mệnh lệnh tình dục của đối phương trong 5 phút", time: 300 },
      { text: "Thực hiện kịch bản BDSM trong 7 phút", time: 420 }
    ]
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer effect
  useEffect(() => {
    let interval = null;
    
    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTime) => {
          if (prevTime <= 1) {
            setIsTimerRunning(false);
            // Play timer end sound
            playSoundIfEnabled('timerEnd');
            return 0;
          }
          
          // Play tick sound for every second
          if (prevTime % 5 === 0 || prevTime <= 5) {
            playSoundIfEnabled('timerTick', { volume: 0.3 });
          }
          
          return prevTime - 1;
        });
      }, 1000);
    } else if (timer === 0) {
      setIsTimerRunning(false);
      clearInterval(interval);
    }
    
    return () => clearInterval(interval);
  }, [isTimerRunning, timer]);

  const startTimer = (seconds) => {
    setTimer(seconds);
    setIsTimerRunning(true);
    setTaskCompleted(false);
    // Play start sound
    playSoundIfEnabled('notification');
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    playSoundIfEnabled('click');
  };

  const completeTask = () => {
    stopTimer();
    setTaskCompleted(true);
    
    // Play success sound
    playSoundIfEnabled('success');
    
    // Update player stats
    if (cardType) {
      setPlayerStats(prev => ({
        ...prev,
        [activePlayer]: {
          ...prev[activePlayer],
          [cardType]: prev[activePlayer][cardType] + 1
        }
      }));
    }
    
    // Switch player for next turn
    setActivePlayer(activePlayer === 'player1' ? 'player2' : 'player1');
  };

  const generateCard = useCallback((type) => {
    const cards = type === 'truth' ? truthCards[filter] : dareCards[filter];
    const usedCardsList = usedCards[type];
    
    // Get unused cards
    const unusedCards = cards.filter((_, index) => !usedCardsList.includes(index));
    
    // If all cards are used, reset
    if (unusedCards.length === 0) {
      setUsedCards(prev => ({ ...prev, [type]: [] }));
      const randomIndex = Math.floor(Math.random() * cards.length);
      setUsedCards(prev => ({ ...prev, [type]: [randomIndex] }));
      return { text: cards[randomIndex], index: randomIndex };
    }
    
    // Select random unused card
    const randomCard = unusedCards[Math.floor(Math.random() * unusedCards.length)];
    const cardIndex = cards.indexOf(randomCard);
    
    // Mark as used
    setUsedCards(prev => ({ ...prev, [type]: [...prev[type], cardIndex] }));
    
    return { text: randomCard, index: cardIndex };
  }, [filter, usedCards]);

  // Check if card might have a timer
  const checkForTimedDare = (cardText) => {
    if (cardType !== 'dare') return null;
    
    // Look through all timed dares for the current filter level
    const timedDaresList = timedDares[filter] || [];
    const matchingDare = timedDaresList.find(dare => cardText === dare.text);
    
    return matchingDare ? matchingDare.time : null;
  };

  const drawCard = (type) => {
    setIsFlipped(false);
    setIsLoading(true);
    setCardType(type);
    setTaskCompleted(false);
    
    // Play card shuffle sound
    playSoundIfEnabled('cardShuffle');
    
    setTimeout(() => {
      const newCard = generateCard(type);
      setCurrentCard(newCard.text);
      
      // Play card flip sound
      playSoundIfEnabled('cardFlip');
      
      setIsFlipped(true);
      setIsLoading(false);
      
      // Check if this is a timed dare
      const timedDuration = checkForTimedDare(newCard.text);
      if (timedDuration) {
        startTimer(timedDuration);
      } else {
        setTimer(0);
        setIsTimerRunning(false);
      }
    }, 500);
  };

  const addCustomCard = () => {
    if (!customCardText.trim()) return;
    
    setCurrentCard(customCardText);
    setCardType(customCardType);
    setIsFlipped(true);
    setShowCustomCard(false);
    setCustomCardText('');
    
    // Play card flip sound
    playSoundIfEnabled('cardFlip');
  };

  const resetCards = () => {
    setUsedCards({ truth: [], dare: [] });
    setCurrentCard(null);
    setIsFlipped(false);
    setCardType(null);
    setTimer(0);
    setIsTimerRunning(false);
    setTaskCompleted(false);
    
    // Play reset sound
    playSoundIfEnabled('notification');
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'mild': return 'from-green-400 to-blue-400';
      case 'spicy': return 'from-yellow-400 to-orange-400';
      case 'hot': return 'from-red-400 to-pink-500';
      case 'extreme': return 'from-purple-600 to-red-600';
      default: return 'from-green-400 to-blue-400';
    }
  };

  const getLevelEmoji = (level) => {
    switch (level) {
      case 'mild': return '😊';
      case 'spicy': return '😏';
      case 'hot': return '🔥';
      case 'extreme': return '💦';
      default: return '😊';
    }
  };

  const getCardTypeColor = (type) => {
    return type === 'truth' ? 'from-blue-500 to-indigo-600' : 'from-red-500 to-pink-600';
  };

  const getActivePlayerName = () => {
    return activePlayer === 'player1' ? player1 : player2;
  };

  const handleFilterChange = (newFilter) => {
    playSoundIfEnabled('click');
    setFilter(newFilter);
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            setShowPrivacy(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>

            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>

              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('click');
                  setShowPrivacy(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showPlayerSetup) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            setShowPlayerSetup(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Thiết Lập Người Chơi
            </h1>

            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên Người Chơi 1:
                </label>
                <input
                  type="text"
                  value={player1}
                  onChange={(e) => setPlayer1(e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  placeholder="Nhập tên..."
                />
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Tên Người Chơi 2:
                </label>
                <input
                  type="text"
                  value={player2}
                  onChange={(e) => setPlayer2(e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none"
                  placeholder="Nhập tên..."
                />
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Người Chơi Đầu Tiên:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      playSoundIfEnabled('click');
                      setActivePlayer('player1');
                    }}
                    className={`p-3 rounded-xl text-center transition-all ${
                      activePlayer === 'player1' 
                        ? 'bg-pink-500 text-white' 
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    {player1}
                  </button>
                  <button
                    onClick={() => {
                      playSoundIfEnabled('click');
                      setActivePlayer('player2');
                    }}
                    className={`p-3 rounded-xl text-center transition-all ${
                      activePlayer === 'player2' 
                        ? 'bg-pink-500 text-white' 
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    {player2}
                  </button>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('success');
                  setShowPlayerSetup(false);
                }}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Xác Nhận
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showCustomCard) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            setShowCustomCard(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Tạo Thẻ Tùy Chỉnh
            </h1>

            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Loại Thẻ:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      playSoundIfEnabled('click');
                      setCustomCardType('truth');
                    }}
                    className={`p-3 rounded-xl text-center transition-all ${
                      customCardType === 'truth' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Truth
                  </button>
                  <button
                    onClick={() => {
                      playSoundIfEnabled('click');
                      setCustomCardType('dare');
                    }}
                    className={`p-3 rounded-xl text-center transition-all ${
                      customCardType === 'dare' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    Dare
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Nội Dung Thẻ:
                </label>
                <textarea
                  value={customCardText}
                  onChange={(e) => setCustomCardText(e.target.value)}
                  className="w-full p-3 rounded-xl bg-white/20 text-white placeholder-pink-200 border border-pink-300/30 focus:border-pink-400 focus:outline-none min-h-[120px]"
                  placeholder="Nhập nội dung thẻ tùy chỉnh của bạn..."
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addCustomCard}
                disabled={!customCardText.trim()}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tạo Thẻ
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('click');
            navigate('/');
          }}
          className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        
        <SoundToggle className="bg-white/20" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center mb-4"
          >
            <h1 className="text-4xl font-bold text-white">
              💕 Truth or Dare 18+ 💕
            </h1>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                playSoundIfEnabled('click');
                setShowPlayerSetup(true);
              }}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-full cursor-pointer"
            >
              <SafeIcon icon={FiUsers} className="text-xl text-white" />
            </motion.div>
          </motion.div>

          {/* Player stats */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-4"
          >
            <div className="flex justify-between">
              <div className={`py-1 px-3 rounded-full text-sm ${activePlayer === 'player1' ? 'bg-pink-500 text-white' : 'text-pink-200'}`}>
                {player1}: {playerStats.player1.truth + playerStats.player1.dare}
              </div>
              <div className={`py-1 px-3 rounded-full text-sm ${activePlayer === 'player2' ? 'bg-pink-500 text-white' : 'text-pink-200'}`}>
                {player2}: {playerStats.player2.truth + playerStats.player2.dare}
              </div>
            </div>
            <div className="mt-2 text-center text-sm text-white">
              <span className="text-pink-300 font-medium">Lượt của:</span> {getActivePlayerName()}
            </div>
          </motion.div>

          <div className="mb-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap justify-center gap-2"
            >
              {['mild', 'spicy', 'hot', 'extreme'].map((level) => (
                <motion.button
                  key={level}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleFilterChange(level)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === level 
                      ? `bg-gradient-to-r ${getLevelColor(level)} text-white` 
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {level === 'mild' ? 'Nhẹ Nhàng' : 
                   level === 'spicy' ? 'Cay Nồng' : 
                   level === 'hot' ? 'Nóng Bỏng' : 'Cực Kỳ Dâm Đãng'} {' '}
                  {getLevelEmoji(level)}
                </motion.button>
              ))}
            </motion.div>
          </div>

          <AnimatePresence mode="wait">
            {currentCard && !isLoading && (
              <motion.div
                key={currentCard}
                initial={{ opacity: 0, rotateY: -90, scale: 0.8 }}
                animate={{ opacity: isFlipped ? 1 : 0, rotateY: isFlipped ? 0 : -90, scale: isFlipped ? 1 : 0.8 }}
                exit={{ opacity: 0, rotateY: 90, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                className={`bg-gradient-to-br ${getCardTypeColor(cardType)} p-8 rounded-3xl shadow-2xl text-white relative overflow-hidden min-h-[300px] flex flex-col justify-between`}
              >
                <div className="absolute top-4 right-4 text-2xl">
                  {getLevelEmoji(filter)}
                </div>
                
                <div className="absolute top-4 left-4 bg-white/20 px-3 py-1 rounded-full">
                  <span className="text-sm font-semibold">
                    {cardType === 'truth' ? 'TRUTH' : 'DARE'}
                  </span>
                </div>
                
                <div className="text-center mt-10">
                  <p className="text-xl leading-relaxed font-medium">
                    {currentCard}
                  </p>
                </div>
                
                {timer > 0 && (
                  <div className="mt-4 bg-white/20 p-3 rounded-xl text-center">
                    <div className="flex items-center justify-center gap-2">
                      <SafeIcon icon={FiClock} className="text-xl" />
                      <span className="text-xl font-bold">{formatTime(timer)}</span>
                    </div>
                    
                    {isTimerRunning ? (
                      <button
                        onClick={stopTimer}
                        className="mt-2 bg-red-500/50 hover:bg-red-500/70 py-1 px-4 rounded-lg text-sm transition-colors"
                      >
                        Dừng
                      </button>
                    ) : (
                      <button
                        onClick={() => startTimer(checkForTimedDare(currentCard))}
                        className="mt-2 bg-green-500/50 hover:bg-green-500/70 py-1 px-4 rounded-lg text-sm transition-colors"
                      >
                        Bắt đầu
                      </button>
                    )}
                  </div>
                )}
                
                <div className="mt-4 flex justify-center">
                  {!taskCompleted ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={completeTask}
                      className="bg-white/20 hover:bg-white/40 text-white font-bold py-2 px-6 rounded-xl transition-all duration-200 flex items-center gap-2"
                    >
                      <SafeIcon icon={FiCheck} className="text-lg" />
                      Hoàn Thành
                    </motion.button>
                  ) : (
                    <div className="bg-green-500/20 text-green-300 py-2 px-6 rounded-xl flex items-center gap-2">
                      <SafeIcon icon={FiCheck} className="text-lg" />
                      Đã hoàn thành
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-[300px] flex items-center justify-center"
              >
                <div className="w-16 h-16 border-t-4 border-pink-500 rounded-full animate-spin"></div>
              </motion.div>
            )}

            {!currentCard && !isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="min-h-[300px] flex flex-col items-center justify-center bg-white/5 backdrop-blur-sm rounded-3xl p-6"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">
                  {getActivePlayerName()}, chọn Truth hoặc Dare!
                </h3>
                <p className="text-pink-200 text-center mb-6">
                  Truth để trả lời câu hỏi thật lòng, Dare để thực hiện thử thách táo bạo
                </p>
                
                <div className="flex gap-4 w-full">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      playSoundIfEnabled('click');
                      drawCard('truth');
                    }}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiHeart} className="text-xl" />
                    TRUTH
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      playSoundIfEnabled('click');
                      drawCard('dare');
                    }}
                    className="flex-1 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiTarget} className="text-xl" />
                    DARE
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4 mt-8">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSoundIfEnabled('click');
                drawCard(cardType || 'truth');
              }}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300"
            >
              <SafeIcon icon={FiShuffle} className="text-xl" />
              Thẻ Mới
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSoundIfEnabled('click');
                setShowCustomCard(true);
              }}
              className="bg-white/20 hover:bg-white/30 text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center transition-all duration-300"
              title="Tạo thẻ tùy chỉnh"
            >
              <SafeIcon icon={FiEdit} className="text-xl" />
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetCards}
              disabled={isLoading}
              className="bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center transition-all duration-300"
              title="Làm mới toàn bộ bộ bài"
            >
              <SafeIcon icon={FiRefreshCw} className="text-xl" />
            </motion.button>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-6"
          >
            <p className="text-pink-200 text-sm">
              Đã sử dụng Truth: {usedCards.truth.length}/{truthCards[filter].length} | 
              Dare: {usedCards.dare.length}/{dareCards[filter].length}
            </p>
          </motion.div>

          <div className="flex items-center justify-between mt-6">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={() => {
                playSoundIfEnabled('click');
                setShowPrivacy(true);
              }}
              className="text-pink-200 text-xs underline flex items-center"
            >
              <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
              Điều khoản & Quyền riêng tư
            </motion.button>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-pink-200 text-xs"
            >
              © 2023 Lux Homestay
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TruthOrDareGame;